"use client";

import { Badge, cn, Switch } from "@oreme/ui";
import { cva, type VariantProps } from "class-variance-authority";
import { useAtom } from "jotai";
import React, { type HTMLAttributes, type ReactElement } from "react";
import { ThemeTypeEnum } from "@/types";
import { themeVisibilityAtomFamily } from "@/store/devices";
import { ThemeBadge } from "@/components";

const themeListItemVariants = cva(
  "flex flex-row gap-2 items-center text-xs rounded uppercase font-semibold",
  {
    variants: {
      visible: {
        true: "",
        false: "opacity-20",
      },
    },
    defaultVariants: {
      visible: true,
    },
  },
);

const themeBadgeVariants = cva(
  "inline-flex item-center rounded-sm h-2 w-6 items-center justify-center",
  {
    variants: {
      color: {
        light: "bg-green-200 text-black",
        dark: "bg-gray-800 text-white",
        green: "bg-green-400 text-white",
        teal: "bg-teal-500 text-white",
        cyan: "bg-cyan-500 text-white",
        yellow: "bg-yellow-500 text-white",
        orange: "bg-orange-500 text-white",
        red: "bg-red-500 text-white",
        blue: "bg-green-500 text-white",
        violet: "bg-violet-500 text-white",
        pink: "bg-pink-500 text-white",
      },
    },
  },
);

type ThemeBadgeVariantsProps = VariantProps<typeof themeBadgeVariants>;
type NativeProps = Omit<
  HTMLAttributes<HTMLLIElement>,
  keyof ThemeBadgeVariantsProps
>;

interface ThemeListItemProps extends NativeProps, ThemeBadgeVariantsProps {
  label: string;
  type: ThemeTypeEnum;
  count?: number;
}

function ThemeListItem({
  className,
  label,
  type,
  count,
}: Readonly<ThemeListItemProps>): ReactElement {
  const [isSelected, setSelected] = useAtom(themeVisibilityAtomFamily(type));

  return (
    <li
      className={cn(
        themeListItemVariants({
          visible: isSelected,
        }),
        className,
      )}
    >
      <ThemeBadge theme={type} />
      {label}
      <div className="ml-auto flex flex-row items-center gap-2">
        <Badge className="ml-auto bg-primary-foreground text-primary-background">
          {count}
        </Badge>
        <span>
          <Switch checked={isSelected} onCheckedChange={setSelected} />
        </span>
      </div>
    </li>
  );
}

export { ThemeListItem, type ThemeListItemProps };
